// src/services/ReportTemplateService.js
// Manages persistence of report template configurations.
// ADM-207: backend dual-write (Wave 27 demo-29) via /api/reporttemplates/state.

import storage from './StorageAdapter';
import { applyDualWriteMixin, makeDualWriteMixin } from './_dualWriteHelpers';
import reportTemplatesApi from '../api/reportTemplates';

const STORAGE_KEY = 'rms_report_templates';
const STATE_KEY = 'templates';

const generateId = (prefix = 'id') =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const DEFAULT_TEMPLATE = {
  id: 'default',
  name: 'Standard Report',
  description: 'Default report template with standard AML compliance sections.',
  sections: [
    {
      id: 'sec_executive',
      title: 'Executive Summary',
      fields: [
        { id: 'f_period', label: 'Reporting Period', type: 'text', required: true, description: '' },
        { id: 'f_entity', label: 'Entity Name', type: 'text', required: true, description: '' },
        { id: 'f_summary', label: 'Summary', type: 'textarea', required: true, description: 'High-level overview of findings.' },
      ],
    },
    {
      id: 'sec_risk',
      title: 'Risk Assessment',
      fields: [
        { id: 'f_risk_level', label: 'Overall Risk Level', type: 'select', required: true, description: '', options: ['Low', 'Medium', 'High', 'Very High'] },
        { id: 'f_risk_date', label: 'Assessment Date', type: 'date', required: true, description: '' },
        { id: 'f_risk_notes', label: 'Notes', type: 'textarea', required: false, description: 'Additional risk observations.' },
      ],
    },
    {
      id: 'sec_controls',
      title: 'Controls & Mitigations',
      fields: [
        { id: 'f_ctrl_desc', label: 'Control Description', type: 'textarea', required: true, description: '' },
        { id: 'f_ctrl_effective', label: 'Controls Effective', type: 'boolean', required: false, description: '' },
      ],
    },
  ],
};

class ReportTemplateService {
  constructor() {
    this._listeners = [];
  }

  _entityKey(_baseKey) {
    return STORAGE_KEY;
  }

  notifyListeners() {
    this._listeners.forEach(fn => fn());
  }

  _notify() { this.notifyListeners(); }

  subscribe(fn) {
    this._listeners.push(fn);
    return () => {
      this._listeners = this._listeners.filter(l => l !== fn);
    };
  }

  getTemplates() {
    const saved = storage.getJSON(STORAGE_KEY, null);
    if (!saved) return [DEFAULT_TEMPLATE];
    return saved;
  }

  async saveTemplates(templates) {
    await this._dualWriteJson(STATE_KEY, templates);
    this._notify();
  }

  async saveTemplate(template) {
    const all = this.getTemplates();
    const idx = all.findIndex(t => t.id === template.id);
    if (idx >= 0) {
      all[idx] = template;
    } else {
      all.push(template);
    }
    await this.saveTemplates(all);
  }

  async deleteTemplate(id) {
    const all = this.getTemplates().filter(t => t.id !== id);
    await this.saveTemplates(all);
  }

  async createTemplate(name, description = '') {
    const template = {
      id: generateId('tmpl'),
      name: name.trim(),
      description: description.trim(),
      sections: [],
    };
    const all = this.getTemplates();
    all.push(template);
    await this.saveTemplates(all);
    return template;
  }

  async addSection(templateId, title) {
    const all = this.getTemplates();
    const tmpl = all.find(t => t.id === templateId);
    if (!tmpl) return;
    tmpl.sections.push({ id: generateId('sec'), title: title.trim(), fields: [] });
    await this.saveTemplates(all);
  }

  async addField(templateId, sectionId, fieldData) {
    const all = this.getTemplates();
    const tmpl = all.find(t => t.id === templateId);
    if (!tmpl) return;
    const sec = tmpl.sections.find(s => s.id === sectionId);
    if (!sec) return;
    sec.fields.push({ id: generateId('fld'), options: [], description: '', ...fieldData });
    await this.saveTemplates(all);
  }

  async initFromBackend() {
    return this._initFromBackend([STATE_KEY], []);
  }
}

applyDualWriteMixin(ReportTemplateService, makeDualWriteMixin({
  storage,
  api: reportTemplatesApi,
  serviceName: 'ReportTemplateService',
}));

const reportTemplateService = new ReportTemplateService();
export { generateId };
export default reportTemplateService;
